// src/contexts/FeatureFlagContext.tsx

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useHospDocData } from "./HospitalDoctorContext";
import {
  getFeatureFlagsByHospitalId,
  patchFeatureFlagsByHospitalId,
} from "../apis/flagfeature";
import { FeatureFlags } from "../utils/types";
import { setCookie } from "../utils/cookies";
import { useUserData } from "./UserContext";
import { hitRefreshToken } from "../apis/user";

export interface FeatureFlagContextInterface extends FeatureFlags {
  isLoading: boolean;
  error: Error | null;
  updateFlags: (patch: Partial<FeatureFlags>) => Promise<void>;
}

const defaultFlags: FeatureFlags = {
  raghvendra_payment_flow: false,
  dummy_parent_hospitals: false,
  specialization_flow: false,
  video_consultation: false,
  payments: false,
  no_extend_session: false,
  no_walkin_button: false,
  language_change_buttons: [],
  admin_panel: false,
  analytics: false,
  prescription: false,
  notification: false,
  multiroom: false,
  mask_patient_name_tv: false,
  announce_first_name_tv: false,
  display_prn_tv: false,
  queue_v2: false,
};

const FeatureFlagContext = createContext<FeatureFlagContextInterface>({
  ...defaultFlags,
  isLoading: false,
  error: null,
  updateFlags: async () => {},
});

export const FeatureFlagProvider = ({ children }: { children: ReactNode }) => {
  const { hospitalID } = useHospDocData();
  const { accessToken } = useUserData();
  const { userData } = useUserData();

  // local state mirrors each flag
  const [flags, setFlags] = useState<FeatureFlags>(defaultFlags);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Validate feature flags data
  const validateFeatureFlags = (data: any): FeatureFlags => {
    if (!data || typeof data !== 'object') {
      console.warn('Invalid feature flags data received:', data);
      return defaultFlags;
    }

    // Helper function to safely convert to boolean
    const safeBoolean = (value: any): boolean => {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') {
        return value.toLowerCase() === 'true' || value === '1';
      }
      if (typeof value === 'number') return value !== 0;
      return Boolean(value);
    };

    // Ensure all required flags are present with proper types
    const validatedFlags: FeatureFlags = {
      raghvendra_payment_flow: safeBoolean(data.raghvendra_payment_flow),
      dummy_parent_hospitals: safeBoolean(data.dummy_parent_hospitals),
      specialization_flow: safeBoolean(data.specialization_flow),
      video_consultation: safeBoolean(data.video_consultation),
      payments: safeBoolean(data.payments),
      no_extend_session: safeBoolean(data.no_extend_session),
      no_walkin_button: safeBoolean(data.no_walkin_button),
      language_change_buttons: Array.isArray(data.language_change_buttons) ? data.language_change_buttons : [],
      admin_panel: safeBoolean(data.admin_panel),
      analytics: safeBoolean(data.analytics),
      prescription: safeBoolean(data.prescription),
      notification: safeBoolean(data.notification),
      multiroom: safeBoolean(data.multiroom),
      mask_patient_name_tv: safeBoolean(data.mask_patient_name_tv),
      announce_first_name_tv: safeBoolean(data.announce_first_name_tv),
      display_prn_tv: safeBoolean(data.display_prn_tv),
      queue_v2: safeBoolean(data.queue_v2),
    };

    return validatedFlags;
  };

  // Fetch
  const loadFlags = async (id: string) => {
    if (!id || id === 'Home') {
      setFlags(defaultFlags);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      if (accessToken && userData) {
        const res = await getFeatureFlagsByHospitalId(id);
        
        console.log("🚩 Feature flags API response:", res);
        if (res?.status === 200 && res.data?.result?.[0]) {
          console.log("🚩 Raw feature flags payload:", res.data.result[0]);
          const validatedFlags = validateFeatureFlags(res.data.result[0]);
          console.log("🚩 Validated feature flags (stored in context):", validatedFlags);
          setFlags(validatedFlags);
        } else if (res?.status === 403) {
          const refresh_data = await hitRefreshToken();
          if (refresh_data?.status === 200) {
            setCookie("accessToken", refresh_data.data.result.access_token, 1);
            const retryRes = await getFeatureFlagsByHospitalId(id);
            if (retryRes?.status === 200 && retryRes.data?.result?.[0]) {
              const validatedFlags = validateFeatureFlags(retryRes.data.result[0]);
              setFlags(validatedFlags);
            } else {
              setFlags(defaultFlags);
            }
          } else {
            setFlags(defaultFlags);
          }
        } else {
          console.warn('Feature flags API returned unexpected response:', res);
          setFlags(defaultFlags);
        }
      } else {
        setFlags(defaultFlags);
      }
    } catch (err: any) {
      console.error("Error fetching feature flags:", err);
      setError(err);
      setFlags(defaultFlags);
    } finally {
      setIsLoading(false);
    }
  };

  // Update
  const updateFlags = async (patch: Partial<FeatureFlags>) => {
    if (!hospitalID) throw new Error("No hospital selected");
    setIsLoading(true);
    setError(null);
    try {
      const updated = await patchFeatureFlagsByHospitalId(hospitalID, patch);
      if (updated) {
        setFlags((prev) => ({ ...prev, ...updated }));
      }
    } catch (err: any) {
      console.error("Error updating feature flags:", err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // reload on hospital change
  useEffect(() => {
    
    if (userData && accessToken && hospitalID) {
      loadFlags(hospitalID);
    } else {
      setFlags(defaultFlags);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, userData, hospitalID]);

  return (
    <FeatureFlagContext.Provider
      value={{
        // spread all flags:
        ...flags,
        // plus helpers:
        isLoading,
        error,
        updateFlags,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = () => useContext(FeatureFlagContext);
